import { ref, computed, watch } from 'vue'
import { s__ } from '@/locale'

/**
 * Composable for managing regular report settings (time and days)
 * Handles hour/minute selection, days checkboxes, and validation logic
 *
 * @param {Object} options - Configuration options
 * @param {string|null} options.initialHour - Initial hour value
 * @param {string|null} options.initialMinute - Initial minute value
 * @param {Array<number>} options.initialDays - Initial days array
 * @param {string} options.preferredLanguage - Preferred language for week day ordering ('en' or 'ru')
 * @param {string} options.translationPrefix - Translation prefix for day labels (default: 'Profile')
 * @returns {Object} - Reactive state and computed properties for regular reports
 */
export function useRegularReport(options = {}) {
  const {
    initialHour = null,
    initialMinute = null,
    initialDays = [],
    preferredLanguage = 'en',
    translationPrefix = 'Profile',
  } = options

  // Local state
  const localHour = ref(initialHour)
  const localMinute = ref(initialMinute)
  const localDays = ref([...initialDays])

  // Track original values for comparison
  const originalHour = ref(initialHour)
  const originalMinute = ref(initialMinute)
  const originalDays = ref([...initialDays])

  // Handle preferredLanguage as either a ref/computed or a string
  const preferredLanguageValue = computed(() => {
    if (
      typeof preferredLanguage === 'object' &&
      preferredLanguage !== null &&
      'value' in preferredLanguage
    ) {
      return preferredLanguage.value || 'en'
    }
    return preferredLanguage || 'en'
  })

  // Week days with locale-based ordering
  const weekDays = computed(() => {
    const isRussian = preferredLanguageValue.value === 'ru'
    const translateDay = dayKey => {
      const scopedKey = `${translationPrefix}|${dayKey}`
      const scopedLabel = s__(scopedKey)
      // Some contexts don't define weekday keys (e.g. ProjectSettings|Mon),
      // so we fall back to shared Profile weekday translations.
      if (scopedLabel === dayKey || scopedLabel === scopedKey) {
        return s__(`Profile|${dayKey}`)
      }
      return scopedLabel
    }

    const days = [
      { label: translateDay('Sun'), value: 0 },
      { label: translateDay('Mon'), value: 1 },
      { label: translateDay('Tue'), value: 2 },
      { label: translateDay('Wed'), value: 3 },
      { label: translateDay('Thu'), value: 4 },
      { label: translateDay('Fri'), value: 5 },
      { label: translateDay('Sat'), value: 6 },
    ]

    // Reorder days based on locale: Russian starts with Monday, English with Sunday
    if (isRussian) {
      const sunday = days.shift()
      days.push(sunday)
    }

    return days
  })

  // Check if time is complete (both hour and minute are set)
  const isTimeComplete = computed(() => {
    return !!(localHour.value && localMinute.value !== null && localMinute.value !== undefined)
  })

  // Check if report is configured (has time or days)
  const hasReport = computed(() => {
    return (
      !!(localHour.value || localMinute.value !== null) ||
      (localDays.value && localDays.value.length > 0)
    )
  })

  // Check if there are unsaved changes
  const hasChanges = computed(() => {
    const normalizeValue = val => (val === null || val === undefined ? null : String(val))
    const normalizeArray = arr => {
      if (!arr || !Array.isArray(arr)) return []
      return arr
        .map(v => Number(v))
        .filter(v => !isNaN(v))
        .sort((a, b) => a - b)
    }

    const origHour = normalizeValue(originalHour.value)
    const origMinute = normalizeValue(originalMinute.value)
    const origDays = normalizeArray(originalDays.value)

    const currentHour = normalizeValue(localHour.value)
    const currentMinute = normalizeValue(localMinute.value)
    const currentDays = normalizeArray(localDays.value)

    return (
      currentHour !== origHour ||
      currentMinute !== origMinute ||
      JSON.stringify(currentDays) !== JSON.stringify(origDays)
    )
  })

  // Auto-set minutes to "00" when hour is selected and minutes is blank
  watch(
    () => localHour.value,
    (newHour, oldHour) => {
      if (newHour && localMinute.value === null) {
        localMinute.value = '00'
      }
      // Clear minute when hour is cleared (nullified)
      if ((newHour === null || newHour === '') && oldHour !== null && oldHour !== '') {
        localMinute.value = null
      }
    }
  )

  // Watch for time changes and clear days if time becomes incomplete
  watch(
    () => [localHour.value, localMinute.value],
    () => {
      if (!isTimeComplete.value && localDays.value.length > 0) {
        localDays.value = []
      }
    }
  )

  // Update original values (call after successful save)
  function updateOriginals() {
    originalHour.value = localHour.value
    originalMinute.value = localMinute.value
    originalDays.value = [...localDays.value]
  }

  // Reset to original values
  function resetToOriginals() {
    localHour.value = originalHour.value
    localMinute.value = originalMinute.value
    localDays.value = [...originalDays.value]
  }

  // Clear all values
  function clear() {
    localHour.value = null
    localMinute.value = null
    localDays.value = []
  }

  // Update from external values (e.g., after prop changes)
  function updateFromProps(hour, minute, days) {
    if (localHour.value !== hour) {
      localHour.value = hour
      originalHour.value = hour
    }
    if (localMinute.value !== minute) {
      localMinute.value = minute
      originalMinute.value = minute
    }
    const newDays = [...(days || [])]
    const currentDaysStr = JSON.stringify([...localDays.value].sort())
    const newDaysStr = JSON.stringify([...newDays].sort())
    if (currentDaysStr !== newDaysStr) {
      localDays.value = newDays
      originalDays.value = newDays
    }
  }

  // Get current values for submission
  function getValues() {
    return {
      hour: localHour.value,
      minute: localMinute.value,
      days: [...localDays.value],
    }
  }

  return {
    // State
    localHour,
    localMinute,
    localDays,
    originalHour,
    originalMinute,
    originalDays,

    // Computed
    weekDays,
    isTimeComplete,
    hasReport,
    hasChanges,

    // Methods
    updateOriginals,
    resetToOriginals,
    clear,
    updateFromProps,
    getValues,
  }
}
